import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import MDAvatar from "components/Basics/MDAvatar";
import { Collapse, Icon } from "@mui/material";
import { useState } from "react";
import TabContent from "./TabContent";

/**
 * Format a date to a readable time string (HH:mm).
 * @param {string|Object} date - The date to format.
 * @returns {string} The formatted time string.
 */
const formatTime = date => {
	if (lod_.isNil(date)) return "";
	const d = date?.$date ? new Date(date.$date) : new Date(date);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

const computeTotalDuration = durations => {
	if (lod_.isNil(durations) || lod_.isEmpty(durations)) return null;
	const total = Object.values(durations).reduce((sum, val) => {
		const n = parseFloat(val);
		return Number.isNaN(n) ? sum : sum + n;
	}, 0);
	return total > 0 ? `${total.toFixed(2)}s` : null;
};

const SOURCE_CONFIG = {
	contact: {
		label: "Contact",
		icon: "person",
		avatarBg: "#e8eaf6",
		avatarColor: "#3949ab",
		bubbleBg: "#ffffff",
		bubbleColor: "#344767",
		align: "flex-start",
		borderRadius: "18px 18px 18px 4px",
		shadow: "0 1px 4px rgba(0,0,0,0.06)"
	},
	bot: {
		label: "Bot",
		icon: "smart_toy",
		avatarBg: "linear-gradient(135deg, #1A73E8, #49a3f1)",
		avatarColor: "#ffffff",
		bubbleBg: "linear-gradient(135deg, #1A73E8, #49a3f1)",
		bubbleColor: "#ffffff",
		align: "flex-end",
		borderRadius: "18px 18px 4px 18px",
		shadow: "0 2px 8px rgba(26,115,232,0.18)"
	},
	cockpit: {
		label: "Agent",
		icon: "support_agent",
		avatarBg: "linear-gradient(135deg, #4CAF50, #67bb6a)",
		avatarColor: "#ffffff",
		bubbleBg: "linear-gradient(135deg, #4CAF50, #67bb6a)",
		bubbleColor: "#ffffff",
		align: "flex-end",
		borderRadius: "18px 18px 4px 18px",
		shadow: "0 2px 8px rgba(76,175,80,0.18)"
	}
};

const DEFAULT_SOURCE_CONFIG = SOURCE_CONFIG.cockpit;

/**
 * Message component to display a single message in a conversation.
 * @param {Object} message - The message object containing header and payload.
 * @param {boolean} showTimestamp - Whether to show the timestamp.
 * @return {JSX.Element} The rendered Message component.
 */
const Message = ({ message, showTimestamp = true, tabMessage = [] }) => {
	const [expanded, setExpanded] = useState(false);

	const header = message?.header;
	const payload = message?.payload;

	if (lod_.isEmpty(header) || lod_.isEmpty(payload)) return null;

	const source = header.source || "contact";
	const config = SOURCE_CONFIG[source] || DEFAULT_SOURCE_CONFIG;
	const isContact = source === "contact";
	const time = formatTime(header.createdAt);
	const feedback = header.feedback ?? null;
	const { text = "", meta = {} } = payload;
	const hasMeta = !lod_.isEmpty(meta);

	/**
	 * Toggle the expanded state of the message meta.
	 */
	const handleBubbleClick = () => {
		if (hasMeta === true) {
			setExpanded(prev => !prev);
		}
	};

	return (
		<MDBox
			display="flex"
			flexDirection="column"
			alignItems={config.align}
			sx={{ mb: 1.5, px: 0.5 }}
		>
			{/* Avatar + Bubble row */}
			<MDBox
				display="flex"
				alignItems="flex-end"
				flexDirection={isContact ? "row" : "row-reverse"}
				sx={{ maxWidth: "82%", gap: 1 }}
			>
				{/* Avatar */}
				<MDAvatar
					size="sm"
					sx={{
						width: 30,
						height: 30,
						background: config.avatarBg,
						color: config.avatarColor,
						flexShrink: 0,
						mb: 0.5
					}}
				>
					<Icon sx={{ fontSize: "16px !important" }}>{config.icon}</Icon>
				</MDAvatar>

				{/* Bubble */}
				<MDBox
					onClick={handleBubbleClick}
					sx={{
						background: config.bubbleBg,
						color: config.bubbleColor,
						borderRadius: config.borderRadius,
						boxShadow: expanded
							? `${config.shadow}, 0 0 0 2px rgba(26,115,232,0.2)`
							: config.shadow,
						px: 2,
						py: 1.25,
						position: "relative",
						wordBreak: "break-word",
						cursor: hasMeta === true ? "pointer" : "default",
						transition: "box-shadow 0.2s ease",
						"&:hover":
							hasMeta === true
								? { boxShadow: `${config.shadow}, 0 0 0 2px rgba(26,115,232,0.12)` }
								: {}
					}}
				>
					<MDBox
						component="span"
						sx={{
							fontSize: "0.84rem",
							lineHeight: 1.55,
							whiteSpace: "normal",
							color: "inherit",
							"& a": {
								color: isContact ? "#1A73E8" : "#ffffff",
								textDecoration: "underline"
							},
							"& div": { display: "block" }
						}}
						dangerouslySetInnerHTML={{ __html: `<div>${text}</div>` }}
					/>
					{/* Context indicator icon */}
					{hasMeta === true && (
						<Icon
							sx={{
								position: "absolute",
								bottom: 4,
								right: 6,
								fontSize: "12px !important",
								color: isContact ? "#bbb" : "rgba(255,255,255,0.5)",
								transition: "transform 0.2s ease",
								transform: expanded ? "rotate(180deg)" : "rotate(0deg)"
							}}
						>
							expand_more
						</Icon>
					)}
				</MDBox>
			</MDBox>

			{/* Timestamp + feedback */}
			{showTimestamp === true && time !== "" && (
				<MDBox display="flex" alignItems="center" sx={{ mt: 0.4, mx: 5.5, gap: 0.5 }}>
					<MDTypography
						variant="caption"
						sx={{
							fontSize: "0.68rem",
							color: "#9e9e9e"
						}}
					>
						{config.label} · {time}{" "}
						{computeTotalDuration(meta.durations) && source === "bot"
							? `· ${computeTotalDuration(meta.durations)}`
							: ""}
					</MDTypography>
					{source === "bot" && !lod_.isNil(feedback) && (
						<Icon
							sx={{
								fontSize: "12px !important",
								color: feedback === "positive" ? "#4CAF50" : "#f44336"
							}}
						>
							{feedback === "positive" ? "thumb_up" : "thumb_down"}
						</Icon>
					)}
				</MDBox>
			)}

			{/* Expandable context panel */}
			{hasMeta === true && (
				<MDBox
					sx={{
						width: "82%",
						ml: isContact ? 5 : 0,
						mr: isContact ? 0 : 5
					}}
				>
					<Collapse
						in={expanded}
						timeout={250}
						unmountOnExit
						sx={{
							width: "100%"
						}}
					>
						<MDBox
							sx={{
								width: "100%",
								mt: 0.75,
								backgroundColor: "#f8f9fb",
								borderRadius: "12px",
								border: "1px solid #e8eaf0"
							}}
						>
							{tabMessage.map((tab, i) => {
								return <TabContent tab={tab} values={message} key={i} displayOnlyExistingValues />;
							})}
						</MDBox>
					</Collapse>
				</MDBox>
			)}
		</MDBox>
	);
};

export default Message;
